import { Box, alpha } from "@mui/material";
import { motion } from "framer-motion";
import { useEffect, useRef } from "react";

const HeroVideoBackground = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    // Ensure video plays properly
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.9; // Slightly slower for cinematic feel
    }
  }, []);

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
      }}
    >
      {/* Video Container with Scale Effect */}
      <motion.div
        initial={{ scale: 1.1, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        >
          <source src="/videos/intro.mp4" type="video/mp4" />
        </video>
      </motion.div>

      {/* Enhanced Gradient Overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `
            linear-gradient(
              135deg,
              rgba(0, 0, 0, 0.9) 0%,
              rgba(0, 0, 0, 0.7) 30%,
              rgba(0, 0, 0, 0.5) 60%,
              rgba(0, 0, 0, 0.8) 100%
            )
          `,
        }}
      />

      {/* Additional Vignette Effect for Cinematic Look */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(
              circle at center,
              transparent 0%,
              transparent 50%,
              rgba(0, 0, 0, 0.4) 100%
            )
          `,
          pointerEvents: "none",
        }}
      />

      {/* Gold Accent Line at Bottom */}
      <motion.div
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ scaleX: 1, opacity: 1 }}
        transition={{ duration: 1.2, delay: 0.5, ease: "easeOut" }}
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "2px",
          transformOrigin: "left",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            background: "linear-gradient(90deg, #C8A96B, transparent)",
            opacity: 0.5,
          }}
        />
      </motion.div>

      {/* Subtle Scanline Effect (Optional) */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `
            repeating-linear-gradient(
              0deg,
              rgba(0, 0, 0, 0.02) 0px,
              rgba(0, 0, 0, 0.02) 2px,
              transparent 2px,
              transparent 4px
            )
          `,
          pointerEvents: "none",
        }}
      />

      {/* Animated Overlay Badge - Optional */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.8 }}
        style={{
          position: "absolute",
          bottom: 30,
          right: 30,
          zIndex: 10,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            background: alpha("#000", 0.6),
            backdropFilter: "blur(10px)",
            borderRadius: "50px",
            padding: "8px 16px",
            border: "1px solid rgba(200,169,107,0.3)",
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#C8A96B",
              animation: "pulse 2s infinite",
              "@keyframes pulse": {
                "0%": {
                  opacity: 1,
                  transform: "scale(1)",
                },
                "50%": {
                  opacity: 0.5,
                  transform: "scale(1.2)",
                },
                "100%": {
                  opacity: 1,
                  transform: "scale(1)",
                },
              },
            }}
          />
          <Box
            component="span"
            sx={{
              color: "#fff",
              fontSize: "0.75rem",
              fontWeight: 500,
              letterSpacing: "0.5px",
            }}
          >
            LIVE
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
};

export default HeroVideoBackground;